import type { PatchableArrayAutoEncoder } from '@simonbackx/simple-encoding';
import { PatchableArray } from '@simonbackx/simple-encoding';
import { useRequestOwner } from '@stamhoofd/networking';
import type { SessionContext } from '@stamhoofd/networking/SessionContext';
import type { Group, RegistrationInvitation, RegistrationInvitationRequest } from '@stamhoofd/structures';
import { PermissionLevel } from '@stamhoofd/structures';
import { useContext } from '../../hooks';
import { CenteredMessage } from '../../overlays/CenteredMessage';
import { Toast } from '../../overlays/Toast';
import type { TableAction } from '../../tables';
import { InMemoryTableAction } from '../../tables';
import type { RegistrationInvitationEvenOrigin } from './useRegistrationInvitationEventListener';
import { RegistrationInvitationEventBus } from './useRegistrationInvitationEventListener';

export function useRegistrationInvitationActionBuilder() {
    const context = useContext();
    const owner = useRequestOwner();

    return (options: {group: Group, eventOrigin: RegistrationInvitationEvenOrigin}) => {
        return new RegistrationInvitationActionBuilder({
            ...options,
            context: context.value,
            owner,
        });
    }
}

export class RegistrationInvitationActionBuilder {
    private readonly group: Group;
    private readonly context: SessionContext;
    private readonly owner: any;
    private readonly eventOrigin: RegistrationInvitationEvenOrigin;

    get hasWrite() {
        return this.context.auth.canAccessGroup(this.group, PermissionLevel.Write)
    }

    constructor(settings: {
        group: Group;
        context: SessionContext;
        owner: any;
        eventOrigin: RegistrationInvitationEvenOrigin
    }) {
        this.group = settings.group;
        this.context = settings.context;
        this.owner = settings.owner;
        this.eventOrigin = settings.eventOrigin;
    }

    getActions() {
        const actions: TableAction<RegistrationInvitation>[] = [];

        const deleteAction = this.getDeleteAction();
        if (deleteAction) {
            actions.push(deleteAction);
        }

        return actions;
    }

    private getDeleteAction(): TableAction<RegistrationInvitation> | null {
        if (!this.hasWrite) {
            return null;
        }

        return getDeleteInvitationAction({
            organizationId: this.group.organizationId,
            context: this.context,
            owner: this.owner,
            eventOrigin: this.eventOrigin
        })
        
    }
}

export function getDeleteInvitationAction({organizationId, context, owner, eventOrigin, afterDelete}: {organizationId: string, context: SessionContext, owner: any, eventOrigin: RegistrationInvitationEvenOrigin | undefined, afterDelete?: () => void}): TableAction<RegistrationInvitation> | null {
        
        return new InMemoryTableAction({
            name: $t('%Kk'),
            destructive: true,
            priority: 1,
            groupIndex: 100,
            needsSelection: true,
            allowAutoSelectAll: false,
            icon: 'trash',
            enabled: true,
            handler: async (invitations: RegistrationInvitation[]) => {
                const message = invitations.length === 1 ? $t('%1Tv', {name: invitations[0].member.name}) : $t('Weet je zeker dat je {count} uitnodigingen wilt verwijderen?', {count: invitations.length});
                const isConfirm = await CenteredMessage.confirm(message, $t('%Kk'));

                if (isConfirm) {
                    deleteInvitations({
                        invitations,
                        organizationId,
                        context,
                        owner,
                        eventOrigin,
                        afterDelete
                    })
                    .catch(console.error);
                }
            },
        });
    }

async function deleteInvitations({invitations, organizationId, context, owner, eventOrigin, afterDelete}: {invitations: RegistrationInvitation[], organizationId: string, context: SessionContext, owner: any, eventOrigin: RegistrationInvitationEvenOrigin | undefined, afterDelete?: () => void}) {
        const patchableArray: PatchableArrayAutoEncoder<RegistrationInvitationRequest> = new PatchableArray();
        invitations.forEach(i => patchableArray.addDelete(i.id));
    
        try {
            await context.getAuthenticatedServerForOrganization(organizationId).request({
                method: 'PATCH',
                path: '/registration-invitations',
                body: patchableArray,
                owner
            });

            RegistrationInvitationEventBus.sendEvent('updated', {groupIds: new Set(invitations.map(i => i.group.id)), origin: eventOrigin}).catch(console.error);

            if (afterDelete) {
                afterDelete();
            }
        } catch (e) {
            console.error(e);
            Toast.fromError(e).show();
            return;
        }
    
        const successMessage = invitations.length === 1 ? $t('%1Tu') : $t('%1U8', { count: invitations.length });
        new Toast(successMessage, 'success green').show();
    }
